// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import { AssessmentReport } from './AssessmentModal';

export const PAGE_SIZE = 20;

export const formatValue = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === '' || value === 'NaN' || value === 'null') {
    return '—';
  }
  return String(value);
};

export const formatScore = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === '' || value === 'NaN' || value === 'null') {
    return '—';
  }
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return '—';
  return String(Math.round(numericValue));
};

// Returns the report's assessment_start_time as a YYYY-MM-DD string (local),
// or null if the report has no valid start time.
export const getReportDateKey = (report: AssessmentReport): string | null => {
  if (!report.assessment_start_time) return null;
  const parsed = new Date(report.assessment_start_time);
  if (Number.isNaN(parsed.getTime())) return null;

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const filterReportsByDateRange = (
  reports: AssessmentReport[],
  dateFrom: string,
  dateTo: string
): AssessmentReport[] => {
  if (!dateFrom && !dateTo) return reports;

  return reports.filter((report) => {
    const dateKey = getReportDateKey(report);

    // Reports with no parsable start time are excluded once a date filter is active.
    if (!dateKey) return false;

    if (dateFrom && dateKey < dateFrom) return false;
    if (dateTo && dateKey > dateTo) return false;

    return true;
  });
};

export const filterReportsByEmail = (reports: AssessmentReport[], emailSearch: string): AssessmentReport[] => {
  const trimmed = emailSearch.trim().toLowerCase();
  if (!trimmed) return reports;

  return reports.filter((report) => (report.email_id || '').toLowerCase().includes(trimmed));
};

// Exports the given reports to an .xlsx file via SheetJS.
// Requires the `xlsx` package: npm install xlsx
export const exportReportsToExcel = async (
  reports: AssessmentReport[],
  fileNamePrefix = 'assessment-reports'
): Promise<void> => {
  const XLSX = await import('xlsx');

  const rows = reports.map((report) => ({
    'User ID': report.user_id,
    'User Name': report.user_name,
    Email: report.email_id,
    'Assessment ID': report.assessment_id,
    'Functional Area': report.functional_area,
    Skill: report.skill,
    'Assessment Mode': report.assessment_mode,
    Status: report.status,
    'Expertise Level': report.expertise_level,
    'Assessed Level': report.assessed_level,
    'Overall Score': report.overall_score,
    'Start Time': report.assessment_start_time,
    'End Time': report.assessment_end_time,
    'Total Tokens Used': report.total_tokens_used,
    'Voice AI Probability': report.voice_ai_probability,
    'Transcript AI Probability': report.transcript_ai_probability,
    'Combined AI Probability': report.combined_ai_probability
  }));

  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Assessments');

  const datetimestamp = new Date().toISOString().replace(/[:.]/g, '-').replace('T', '_').split('Z')[0]; // Format: YYYY-MM-DD_HH-MM-SS
  XLSX.writeFile(workbook, `${fileNamePrefix}-${datetimestamp}.xlsx`);
};
